import { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";

const fmt = (n) =>
    new Intl.NumberFormat("en-AE", { maximumFractionDigits: 0 }).format(n);

const CONTROLS = {
    gold: {
        track: "[&_[role=slider]]:bg-gold [&_[role=slider]]:border-gold [&_[role=slider]]:shadow-[0_0_0_4px_rgba(181,137,62,0.18)] [&>span:first-child]:h-1 [&>span:first-child]:bg-cream-deep [&_[data-orientation=horizontal]>span]:bg-gold",
        top: "bg-gold",
        text: "text-gold",
    },
    forest: {
        track: "[&_[role=slider]]:bg-forest [&_[role=slider]]:border-forest [&_[role=slider]]:shadow-[0_0_0_4px_rgba(30,77,64,0.18)] [&>span:first-child]:h-1 [&>span:first-child]:bg-cream-deep [&_[data-orientation=horizontal]>span]:bg-forest",
        top: "bg-forest",
        text: "text-forest",
    },
    coral: {
        track: "[&_[role=slider]]:bg-coral [&_[role=slider]]:border-coral [&_[role=slider]]:shadow-[0_0_0_4px_rgba(212,88,68,0.18)] [&>span:first-child]:h-1 [&>span:first-child]:bg-cream-deep [&_[data-orientation=horizontal]>span]:bg-coral",
        top: "bg-coral",
        text: "text-coral",
    },
};

function Control({ accent, label, value, meta, children, min, max, step, onChange, sliderProps }) {
    const c = CONTROLS[accent];
    return (
        <div className="relative bg-white border border-cream-deep p-6 md:p-7 flex flex-col">
            <div className={`absolute top-0 left-0 right-0 h-1 ${c.top}`} />
            <div className="flex items-start justify-between mb-5">
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-ink-muted mb-2">
                        {label}
                    </div>
                    <div className="font-serif text-3xl md:text-4xl text-ink leading-none">
                        {children}
                    </div>
                </div>
                <div className={`font-mono text-[10px] uppercase tracking-[0.25em] ${c.text} text-right max-w-[40%]`}>
                    {meta}
                </div>
            </div>
            <div className="mt-auto">
                <Slider
                    {...sliderProps}
                    value={[value]}
                    onValueChange={(v) => onChange(v[0])}
                    min={min}
                    max={max}
                    step={step}
                    className={c.track}
                />
            </div>
        </div>
    );
}

export default function Calculator() {
    const [amount, setAmount] = useState(750000);
    const [rate, setRate] = useState(6.49);
    const [years, setYears] = useState(4);

    const { emi, totalInterest, totalPayable, principalPct } = useMemo(() => {
        const months = years * 12;
        const monthlyRate = rate / 100 / 12;
        const emi =
            (amount * monthlyRate * Math.pow(1 + monthlyRate, months)) /
            (Math.pow(1 + monthlyRate, months) - 1);
        const totalPayable = emi * months;
        const totalInterest = totalPayable - amount;
        return {
            emi: Math.round(emi),
            totalInterest: Math.round(totalInterest),
            totalPayable: Math.round(totalPayable),
            principalPct: Math.max(6, Math.min(94, (amount / totalPayable) * 100)),
        };
    }, [amount, rate, years]);

    return (
        <section
            id="calculator"
            data-testid="calculator-section"
            className="relative py-20 md:py-28 bg-cream-warm border-y border-cream-deep"
        >
            <div className="max-w-[1280px] mx-auto px-6 md:px-10">
                {/* Header */}
                <div className="grid md:grid-cols-12 gap-8 mb-12">
                    <div className="md:col-span-7">
                        <div className="flex items-center gap-3 mb-5">
                            <span className="w-8 h-px bg-gold" />
                            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">
                                Capital Simulator
                            </span>
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl leading-[1] tracking-tight font-medium text-ink">
                            Model the math.<br />
                            <em className="italic text-gold font-normal">Before</em> the conversation.
                        </h2>
                    </div>
                    <div className="md:col-span-4 md:col-start-9 flex items-end">
                        <p className="text-ink-muted font-light leading-relaxed text-lg">
                            Drag to estimate any structure. No account, no credit pull. When ready, we will build a bespoke offer.
                        </p>
                    </div>
                </div>

                {/* Control row */}
                <div className="grid md:grid-cols-3 gap-5">
                    <Control
                        accent="gold"
                        label="Capital Required"
                        value={amount}
                        min={50000}
                        max={60000000}
                        step={10000}
                        onChange={setAmount}
                        meta={`${((amount / 60000000) * 100).toFixed(1)}% of cap`}
                        sliderProps={{ "data-testid": "calc-amount-slider" }}
                    >
                        <span className="text-ink-muted text-xl mr-1">AED</span>
                        <span data-testid="calc-amount-value">{fmt(amount)}</span>
                    </Control>

                    <Control
                        accent="forest"
                        label="Profit Rate"
                        value={rate}
                        min={4.49}
                        max={14.99}
                        step={0.01}
                        onChange={setRate}
                        meta="Annual · Reducing"
                        sliderProps={{ "data-testid": "calc-rate-slider" }}
                    >
                        <span data-testid="calc-rate-value">{rate.toFixed(2)}</span>
                        <span className="text-forest"> %</span>
                    </Control>

                    <Control
                        accent="coral"
                        label="Tenure"
                        value={years}
                        min={1}
                        max={30}
                        step={1}
                        onChange={setYears}
                        meta={`${years * 12} months`}
                        sliderProps={{ "data-testid": "calc-tenure-slider" }}
                    >
                        <span data-testid="calc-tenure-value">{years}</span>
                        <span className="text-coral"> {years === 1 ? "year" : "years"}</span>
                    </Control>
                </div>

                {/* Result ledger */}
                <div className="mt-5 relative bg-ink text-cream overflow-hidden">
                    <div className="absolute -top-24 -right-24 w-80 h-80 bg-gold/15 rounded-full blur-3xl pointer-events-none" />
                    <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-forest/20 rounded-full blur-3xl pointer-events-none" />

                    <div className="relative grid md:grid-cols-12 gap-8 p-7 md:p-12">
                        {/* Big monthly number */}
                        <div className="md:col-span-4 flex flex-col justify-center md:border-r border-white/10 md:pr-8">
                            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold mb-3">
                                Estimated Monthly
                            </div>
                            <div className="font-serif text-6xl md:text-7xl text-white leading-none">
                                <span className="text-cream/50 text-2xl mr-1.5 align-top">AED</span>
                                <span data-testid="calc-emi-value">{fmt(emi)}</span>
                            </div>
                            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-cream/60 mt-3">
                                per month · over {years * 12} months
                            </div>
                        </div>

                        {/* Breakdown + bar */}
                        <div className="md:col-span-8 flex flex-col justify-center gap-6">
                            {/* Stacked composition bar */}
                            <div>
                                <div className="flex justify-between items-baseline mb-2.5">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream/60">
                                        Composition of total
                                    </span>
                                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream/60">
                                        {principalPct.toFixed(0)}% capital · {(100 - principalPct).toFixed(0)}% profit
                                    </span>
                                </div>
                                <div className="flex h-3 w-full overflow-hidden bg-white/10">
                                    <div
                                        className="h-full bg-gold transition-all duration-300"
                                        style={{ width: `${principalPct}%` }}
                                    />
                                    <div
                                        className="h-full bg-forest-soft transition-all duration-300"
                                        style={{ width: `${100 - principalPct}%` }}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-4 pt-5 border-t border-white/15">
                                <div>
                                    <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-cream/60 mb-1.5">
                                        <span className="w-2 h-2 bg-gold inline-block" /> Principal
                                    </div>
                                    <div className="font-serif text-xl md:text-2xl text-white">AED {fmt(amount)}</div>
                                </div>
                                <div>
                                    <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-cream/60 mb-1.5">
                                        <span className="w-2 h-2 bg-forest-soft inline-block" /> Total Profit
                                    </div>
                                    <div className="font-serif text-xl md:text-2xl text-gold">AED {fmt(totalInterest)}</div>
                                </div>
                                <div>
                                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/60 mb-1.5">
                                        Total Payable
                                    </div>
                                    <div className="font-serif text-xl md:text-2xl text-white">AED {fmt(totalPayable)}</div>
                                </div>
                            </div>

                            <a
                                href="#contact"
                                data-testid="calc-cta-button"
                                className="group inline-flex items-center justify-center gap-2 bg-gold text-white px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.25em] hover:bg-gold-soft transition-colors w-full sm:w-auto self-start"
                            >
                                Receive Bespoke Offer
                                <span className="group-hover:translate-x-1 transition-transform">→</span>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
